import {materialConnectionDefinition} from "#material/nodes/base/connection/definition"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {SocketValueType} from "#material/sockets/socket-value-type"
import type {MaterialContext} from "#material/types/context"
import type {Evaluator, GraphNodeDefinition} from "@cm/graph-v3"
import {registerMaterialGraphNode} from "#material/register"
import {GraphNode, lazyDefinition} from "@cm/graph-v3"
import type {ShaderNode} from "#material/nodes/base/material/validation/shader-node"
import type {MaterialConnectionLike, MaterialNodeLike} from "#material/types/material-connection"
import type {Three as THREE} from "#material/three/exports"

const definition = lazyDefinition(() => materialConnectionDefinition())

@registerMaterialGraphNode
export class MaterialConnection<Returns>
    extends GraphNode<
        [ShaderNode, string] | THREE.Material | THREE.Node,
        MaterialContext,
        {
            key: string
            value: MaterialNodeLike<{
                inputs: any
                inputsExtensions: {}
                settings: any
                settingsExtensions: {}
                outputs: {[Key: string]: SocketValueType}
            }>
        }
    >
    implements MaterialConnectionLike<Returns>
{
    public override get definition(): GraphNodeDefinition<
        [ShaderNode, string] | THREE.Material | THREE.Node,
        {
            key: string
            value: MaterialNodeLike<{
                inputs: any
                inputsExtensions: {}
                settings: any
                settingsExtensions: {}
                outputs: {[Key: string]: SocketValueType}
            }>
        }
    > {
        return definition()
    }

    public legacyId?: number
    public __typeBrand?: Returns // Only used to enforce type safety

    override toJSON(): object {
        return {
            [`${this.nodeClass}`]: {
                id: this.id,
                legacyId: this.legacyId,
                ...this.parameters,
            },
        }
    }

    override toString() {
        return `MaterialConnection<${this.source?.nodeClass ?? "?"} "${this.sourceParameter}" --> "${this.destinationParameter}" ${this.destination?.nodeClass ?? "?"}>`
    }

    override run = async (evaluator: Evaluator<MaterialContext>) => {
        const value = await evaluator.get(this.source)
        const result = value[this.sourceParameter] as [ShaderNode, string] | THREE.Material | THREE.Node | undefined
        if (!result) {
            throw new Error(`No return value found for node type "${this.source.nodeClass}" in Cycles context for key "${this.sourceParameter}"`)
        }
        return result
    }

    override runSync = (evaluator: Evaluator<MaterialContext>): [ShaderNode, string] | THREE.Material | THREE.Node => {
        throw new Error("MaterialConnection cannot be run synchronously")
    }

    get source(): MaterialNodeLike<{
        inputs: any
        inputsExtensions: {}
        settings: any
        settingsExtensions: {}
        outputs: {[Key: string]: SocketValueType}
    }> {
        return this.parameters.value
    }

    get destination(): MaterialNode<{
        inputs: any
        inputsExtensions: {}
        settings: any
        settingsExtensions: {}
        outputs: {[Key: string]: SocketValueType}
    }> | null {
        // TODO: stricter type checking (node -> connection -> node etc.)
        // ignore MaterialGraph nodes as parents, they are not actual connections
        const actualParent = [...this.parents].find((parent) => parent instanceof MaterialNode)
        if (!actualParent) {
            return null
        }
        return actualParent as MaterialNode<{
            inputs: any
            inputsExtensions: {}
            settings: any
            settingsExtensions: {}
            outputs: {[Key: string]: SocketValueType}
        }> | null
    }

    get sourceParameter() {
        return this.parameters.key
    }

    get destinationParameter() {
        return Object.entries(this.destination?.parameters.inputs ?? {}).find(([_name, value]) => {
            return value instanceof GraphNode && value.instanceId === this.instanceId
        })?.[0]
    }

    public get data() {
        const parentsExcludingRoot = Array.from(this.parents).filter((parent) => parent.nodeClass !== "MaterialGraph")
        if (parentsExcludingRoot.length === 1 && this.children.size === 1) {
            const parent = parentsExcludingRoot[0]
            const child = Array.from(this.children)[0]
            const inputEntries = Object.entries(parent.parameters.inputs as {[key: string]: unknown})
            const inputInParent = inputEntries.find(([_name, value]) => {
                return value instanceof MaterialConnection && value.instanceId === this.instanceId
            })
            if (inputInParent) {
                return {
                    id: this.instanceId,
                    source: {
                        nodeClass: child.nodeClass,
                        nodeId: child.instanceId,
                        socketId: this.sourceParameter,
                        color: child instanceof MaterialNode ? child.colorForSocket(this.sourceParameter, "output") : undefined,
                    },
                    destination: {
                        nodeClass: parent.nodeClass,
                        nodeId: parent.instanceId,
                        socketId: inputInParent[0],
                        color: parent instanceof MaterialNode ? parent.colorForSocket(inputInParent[0], "input") : undefined,
                    },
                }
            } else {
                throw new Error(`Invalid material connection: ${this}`)
            }
        } else {
            throw new Error(
                `MaterialConnection node is not a 1-to-1 connection: ${this.parents.size} parents (${[...this.parents].map((node) => node.nodeClass).join(",")}), ${this.children.size} children (${[...this.children].map((node) => node.nodeClass).join(",")})`,
            )
        }
    }
}
